import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { CancelButton } from 'src/components/Buttons';
import { Content } from 'src/contexts/ContentContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { Button, Radio, RadioGroup, Stack } from 'src/core/ui';
import { useAccountingEventExport } from 'src/hooks/api/useAccountingEventExport';
import { LoadingIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';

import * as styles from './AccountingAuditExport.css';

export interface StreamOption {
  value: string;
  label: ReactNode;
}

const defaultStreamOptions: StreamOption[] = [
  {
    value: 'invoice_void',
    label: <Content id={ContentId.ExportInvoiceVoid} />,
  },
  { value: 'invoice', label: <Content id={ContentId.ExportInvoice} /> },
];

export interface AccountingAuditExportProps {
  entityId: number;
  isOpen: boolean;
  onClose: () => void;
  streamOptions?: StreamOption[];
}

export const AccountingAuditExport = ({
  entityId,
  isOpen,
  onClose,
  streamOptions,
}: AccountingAuditExportProps) => {
  const resolvedStreamOptions = streamOptions ?? defaultStreamOptions;

  const [selectedStream, setSelectedStream] = useState<string>(
    resolvedStreamOptions[0].value
  );
  const [createDateFrom, setCreateDateFrom] = useState<string>('');
  const [createDateTo, setCreateDateTo] = useState<string>('');

  const { startExport, isExporting, hasFeature } = useAccountingEventExport({
    stream: selectedStream,
    createDateFrom: createDateFrom || undefined,
    createDateTo: createDateTo || undefined,
    entityId,
  });

  // Track whether an export was started so we can auto-close on completion
  const exportStartedRef = useRef(false);

  useEffect(() => {
    if (isExporting) {
      exportStartedRef.current = true;
    } else if (exportStartedRef.current) {
      // Export finished (success or error) — close the modal
      exportStartedRef.current = false;
      onClose();
    }
  }, [isExporting, onClose]);

  const handleExport = useCallback(() => {
    startExport();
  }, [startExport]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!hasFeature) {
    return null;
  }

  return (
    <GenericDialog
      size="md"
      isOpen={isOpen}
      onCancel={isExporting ? undefined : handleCancel}
      header={<Content id={ContentId.Export} />}
      footer={
        <>
          <CancelButton disabled={isExporting} onClick={handleCancel} />
          <Button
            variant="regular"
            disabled={isExporting}
            onClick={handleExport}
          >
            {isExporting ? (
              <span className={styles.exportButtonContent}>
                <LoadingIcon className={styles.exportSpinner} />
                <Content id={ContentId.Exporting} />
              </span>
            ) : (
              <Content id={ContentId.Export} />
            )}
          </Button>
        </>
      }
    >
      <div className={styles.dialogBody}>
        <RadioGroup
          value={selectedStream}
          onValueChange={(value) => setSelectedStream(value)}
        >
          <Stack direction="row" gap="xl">
            {resolvedStreamOptions.map((option) => (
              <Radio
                key={option.value}
                value={option.value}
                disabled={isExporting}
                label={option.label}
              />
            ))}
          </Stack>
        </RadioGroup>

        <div className={styles.dateRangeSection}>
          <div className={styles.dateRangeLabel}>
            <Content id={ContentId.ExportTimeRange} />
          </div>
          <Stack direction="row" gap="m" alignItems="center">
            <input
              type="date"
              className={styles.dateInput}
              value={createDateFrom}
              onChange={(e) => setCreateDateFrom(e.target.value)}
              disabled={isExporting}
            />
            <span className={styles.dateArrow}>&rarr;</span>
            <input
              type="date"
              className={styles.dateInput}
              value={createDateTo}
              onChange={(e) => setCreateDateTo(e.target.value)}
              disabled={isExporting}
            />
          </Stack>
        </div>
      </div>
    </GenericDialog>
  );
};
